import {
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import type {
	MetaAdsCreateCustomListAudienceDto,
	MetaAdsCreateEngagementAudienceDto,
	MetaAdsCreateLookalikeAudienceDto,
	MetaAdsCreateWebsiteAudienceDto,
} from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { QueryKeys } from "../LinkGen";

export const useAudiences = () => {
	const company = useCompany();
	return useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.Audiences(company.id),
		queryFn: () => Api.GetMetaAdsAudiences().then((r) => r.data),
	});
};

/** Non-suspense variant for wizard steps outside a dedicated error boundary. */
export const useAudiencesQuery = () => {
	const company = useCompany();
	return useQuery({
		queryKey: QueryKeys.Integrations.MetaAds.Audiences(company.id),
		queryFn: () => Api.GetMetaAdsAudiences().then((r) => r.data),
	});
};

export const useAudienceActions = () => {
	const company = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();

	const invalidate = () =>
		queryClient.invalidateQueries({
			queryKey: QueryKeys.Integrations.MetaAds.Audiences(company.id),
		});

	const createCustomList = useMutation({
		mutationFn: (dto: MetaAdsCreateCustomListAudienceDto) =>
			Api.CreateMetaAdsCustomListAudience(dto).then((r) => r.data),
		onSuccess: () => {
			invalidate();
			emit({
				type: AlertType.SUCCESS,
				content: "Custom list audience created.",
			});
		},
		onError: (error) => {
			const msg = error instanceof Error ? error.message : "Unknown error";
			emit({
				type: AlertType.ERROR,
				content: `Failed to create audience: ${msg}`,
			});
		},
	});

	const createWebsite = useMutation({
		mutationFn: (dto: MetaAdsCreateWebsiteAudienceDto) =>
			Api.CreateMetaAdsWebsiteAudience(dto).then((r) => r.data),
		onSuccess: () => {
			invalidate();
			emit({ type: AlertType.SUCCESS, content: "Website audience created." });
		},
		onError: (error) => {
			const msg = error instanceof Error ? error.message : "Unknown error";
			emit({
				type: AlertType.ERROR,
				content: `Failed to create audience: ${msg}`,
			});
		},
	});

	const createEngagement = useMutation({
		mutationFn: (dto: MetaAdsCreateEngagementAudienceDto) =>
			Api.CreateMetaAdsEngagementAudience(dto).then((r) => r.data),
		onSuccess: () => {
			invalidate();
			emit({
				type: AlertType.SUCCESS,
				content: "Engagement audience created.",
			});
		},
		onError: (error) => {
			const msg = error instanceof Error ? error.message : "Unknown error";
			emit({
				type: AlertType.ERROR,
				content: `Failed to create audience: ${msg}`,
			});
		},
	});

	const createLookalike = useMutation({
		mutationFn: (dto: MetaAdsCreateLookalikeAudienceDto) =>
			Api.CreateMetaAdsLookalikeAudience(dto).then((r) => r.data),
		onSuccess: () => {
			invalidate();
			emit({ type: AlertType.SUCCESS, content: "Lookalike audience created." });
		},
		onError: (error) => {
			const msg = error instanceof Error ? error.message : "Unknown error";
			emit({
				type: AlertType.ERROR,
				content: `Failed to create audience: ${msg}`,
			});
		},
	});

	const renameAudience = useMutation({
		mutationFn: ({ id, name }: { id: string; name: string }) =>
			Api.RenameMetaAdsAudience(id, { name }).then((r) => r.data),
		onSuccess: () => {
			invalidate();
			emit({ type: AlertType.SUCCESS, content: "Audience renamed." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to rename audience." });
		},
	});

	const deleteAudience = useMutation({
		mutationFn: (id: string) =>
			Api.DeleteMetaAdsAudience(id).then((r) => r.data),
		onSuccess: () => {
			invalidate();
			emit({ type: AlertType.SUCCESS, content: "Audience deleted." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to delete audience." });
		},
	});

	const refreshStatus = useMutation({
		mutationFn: (id: string) =>
			Api.RefreshMetaAdsAudienceStatus(id).then((r) => r.data),
		onSuccess: () => {
			invalidate();
			emit({ type: AlertType.SUCCESS, content: "Audience status refreshed." });
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to refresh status." });
		},
	});

	return {
		createCustomList,
		createWebsite,
		createEngagement,
		createLookalike,
		renameAudience,
		deleteAudience,
		refreshStatus,
	};
};
